import React from "react";
import { Document, Page, View, Text, StyleSheet, PDFDownloadLink, Link } from "@react-pdf/renderer";

// ---- Layout constants ----
const HEADER_H = 56;
const THEAD_H = 36;
const TOP_SPACE = HEADER_H + THEAD_H + 16;

const styles = StyleSheet.create({
    page: { paddingTop: TOP_SPACE, paddingHorizontal: 20, paddingBottom: 40, fontSize: 9 },
    fixedHeader: { position: "absolute", top: 20, left: 20, right: 20 },
    title: { fontSize: 16, fontWeight: "bold", marginBottom: 2 },
    subTitle: { flexDirection: "row", justifyContent: "space-between" },
    subTitleText: { fontSize: 10 },

    thead: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderColor: "#555",
        backgroundColor: "#f0f0f0",
        paddingVertical: 4,
        marginTop: 8,
    },

    th: { fontWeight: "bold", paddingHorizontal: 3, borderRightWidth: 1, borderColor: "#ccc", lineHeight: 1, justifyContent: 'center' },
    tr: { flexDirection: "row", borderBottomWidth: 1, borderColor: "#eee" },
    td: { paddingHorizontal: 3, paddingVertical: 2, borderRightWidth: 1, borderColor: "#f2f2f2", lineHeight: 0.9 },

    // columns
    c_dept: { flex: 25 }, c_emp: { flex: 28 }, c_desig: { flex: 20 },
    c_prWDays: { flex: 10, textAlign: "right" }, c_lCount: { flex: 8, textAlign: "right" },
    c_lLeaves: { flex: 8, textAlign: "right" }, c_empSalary: { flex: 12, textAlign: "right" },
    c_perDay: { flex: 10, textAlign: "right" }, c_OT: { flex: 10, textAlign: "right" },
    c_OTApH: { flex: 10, textAlign: "right" }, c_OTAmt: { flex: 10, textAlign: "right" },
    c_GrossSalary: { flex: 12, textAlign: "right" }, c_allowances_amount: { flex: 12, textAlign: "right" },
    c_advances_amount: { flex: 12, textAlign: "right" }, c_Total: { flex: 12, textAlign: "right" },

    // section header (department)
    deptHeader: {
        flexDirection: "row",
        backgroundColor: "#fafafa",
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: "#bbb",
    },
    deptTitle: { fontWeight: "bold" },

    // subtotal row
    subtotalRow: {
        flexDirection: "row",
        backgroundColor: "#fbfbfb",
        borderTopWidth: 1,
        borderColor: "#ddd",
    },
    totalRow: {
        flexDirection: "row",
        borderTopWidth: 1,
        borderColor: "#bbb",
        backgroundColor: "#f5f5f5",
    },
    bold: { fontWeight: "bold" },

    footerBar: {
        position: "absolute",
        bottom: 10,
        left: 20,
        right: 20,
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 9,
    },
});

// --- helpers ---
const safeNum = (v) => {
    if (v === null || v === undefined) return 0;
    const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : Number(v);
    return Number.isFinite(n) ? n : 0;
};
const sumBy = (arr, key) => arr.reduce((t, r) => t + safeNum(r?.[key]), 0);

// minutes -> HH:MM:SS (for overtime totals if needed)
const minutesToHHMMSS = (mins) => {
    const totalSeconds = Math.round(safeNum(mins) * 60);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad = (x) => String(x).padStart(2, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

// If you pass raw rows, we build {departments, grandTotals}
function buildGrouped(rows) {
    const FIELDS = [
        "present_working_days",
        "late_days",
        "late_leaves",
        "empSalary",
        "per_day_salary",
        "overtime_minutes",
        "overtime_amount",
        "total_pay_gross",
        "allowances_amount",
        "advances_amount",
        "total_pay_net",
    ];

    const toNum = (v) => safeNum(v);
    const emptyTotals = () => FIELDS.reduce((a, k) => ((a[k] = 0), a), {});
    const map = new Map();

    for (const r of rows || []) {
        const dept = (r.deptName || "UNKNOWN").trim();
        if (!map.has(dept)) map.set(dept, { rows: [], totals: emptyTotals() });
        const b = map.get(dept);
        b.rows.push(r);
        for (const f of FIELDS) b.totals[f] += toNum(r[f]);
    }
    const departments = Array.from(map.entries()).map(([deptName, v]) => ({
        deptName,
        countEmployees: v.rows.length,
        rows: v.rows,
        totals: {
            ...v.totals,
            overtime_hhmm_total: minutesToHHMMSS(v.totals.overtime_minutes),
        },
    }));

    // grand totals
    const g = emptyTotals();
    for (const d of departments) for (const f of FIELDS) g[f] += d.totals[f];
    const grandTotals = {
        ...g,
        overtime_hhmm_total: minutesToHHMMSS(g.overtime_minutes),
    };

    // unitDescription & working_days_full_month for header (best-effort from first row)
    const first = rows?.[0] || {};
    return { departments, grandTotals, header: { unitDescription: first.unitDescription, working_days_full_month: first.working_days_full_month } };
}

// Normalize prop: accept either raw rows (array) or grouped object
function normalizeData(input) {
    if (Array.isArray(input)) {
        const built = buildGrouped(input);
        return { departments: built.departments, grandTotals: built.grandTotals, header: built.header };
    }
    // already grouped
    const firstRow = input?.departments?.[0]?.rows?.[0] || {};
    const header = {
        unitDescription: firstRow?.unitDescription,
        working_days_full_month: firstRow?.working_days_full_month,
    };
    return { departments: input?.departments || [], grandTotals: input?.grandTotals || {}, header };
}

// ==== PDF Document ====
const AttendanceReportDoc = ({ data, title = "Salary Summary Report", company = "WHD", reportDate }) => {
    const { departments, grandTotals, header } = normalizeData(data);

    return (
        <Document>
            <Page size="LEGAL" orientation="landscape" style={styles.page}>
                {/* Header + Table Headings */}
                <View style={styles.fixedHeader} fixed>
                    <Text style={styles.title}>{company}</Text>
                    <View fixed style={styles.subTitle}>
                        <Text style={[styles.subTitleText, { fontSize: 12, fontWeight: "bold" }]}>{title}</Text>
                        <Text style={[styles.subTitleText, { fontWeight: "bold" }]}>
                            {`Unit: ${header?.unitDescription ?? "-"}`}
                        </Text>
                        <Text style={[styles.subTitleText, { fontWeight: "bold" }]}>
                            Total Working Days: {header?.working_days_full_month ?? "-"}
                        </Text>
                    </View>

                    <View style={styles.thead}>
                        <View style={[styles.th, styles.c_dept, { textAlign: 'center' }]}>
                            <Text>Department</Text>
                        </View>
                        <View style={[styles.th, styles.c_emp, { textAlign: 'center' }]}>
                            <Text>Name</Text>
                        </View>
                        <View style={[styles.th, styles.c_desig, { textAlign: 'center' }]}>
                            <Text>Designation</Text>
                        </View>
                        <View style={[styles.th, styles.c_prWDays, { textAlign: 'center' }]}>
                            <Text>Pr W.Days</Text>
                        </View>
                        <View style={[styles.th, styles.c_lCount, { textAlign: 'center' }]}>
                            <Text>L.Count</Text>
                        </View>
                        <View style={[styles.th, styles.c_lLeaves, { textAlign: 'center' }]}>
                            <Text>L.Leaves</Text>
                        </View>
                        <View style={[styles.th, styles.c_empSalary, { textAlign: 'center' }]}>
                            <Text>Salary</Text>
                        </View>
                        <View style={[styles.th, styles.c_perDay, { textAlign: 'center' }]}>
                            <Text>Per Day</Text>
                        </View>
                        <View style={[styles.th, styles.c_OT, { textAlign: 'center' }]}>
                            <Text>Over Time</Text>
                        </View>
                        <View style={[styles.th, styles.c_OTApH, { textAlign: 'center' }]}>
                            <Text>OT Amt / Hour</Text>
                        </View>
                        <View style={[styles.th, styles.c_OTAmt, { textAlign: 'center' }]}>
                            <Text>OT Amt</Text>
                        </View>
                        <View style={[styles.th, styles.c_GrossSalary, { textAlign: 'center' }]}>
                            <Text>Gross</Text>
                        </View>
                        <View style={[styles.th, styles.c_allowances_amount, { textAlign: 'center' }]}>
                            <Text>Allowances</Text>
                        </View>
                        <View style={[styles.th, styles.c_advances_amount, { textAlign: 'center' }]}>
                            <Text>Advances</Text>
                        </View>
                        <View style={[styles.th, styles.c_Total, { textAlign: 'center' }]}>
                            <Text>Salary Payble</Text>
                        </View>
                        {/* <Text style={[styles.th, styles.c_emp, { textAlign: 'center' }]}>Name</Text>
                        <Text style={[styles.th, styles.c_prWDays, { verticalAlign: 'center' }]}>Pr W.Days</Text>
                        <Text style={[styles.th, styles.c_lCount, { textAlign: 'center' }]}>L.Count</Text>
                        <Text style={[styles.th, styles.c_lLeaves, { textAlign: 'center' }]}>L.Leaves</Text>
                        <Text style={[styles.th, styles.c_empSalary, { textAlign: 'center' }]}>Salary</Text>
                        <Text style={[styles.th, styles.c_perDay, { textAlign: 'center' }]}>Per Day</Text>
                        <Text style={[styles.th, styles.c_OT, { textAlign: 'center' }]}>Over Time</Text>
                        <Text style={[styles.th, styles.c_OTApH, { textAlign: 'center' }]}>OT Amt / Hour</Text>
                        <Text style={[styles.th, styles.c_OTAmt, { textAlign: 'center' }]}>OT Amt</Text>
                        <Text style={[styles.th, styles.c_GrossSalary, { textAlign: 'center' }]}>Gross</Text>
                        <Text style={[styles.th, styles.c_allowances_amount, { textAlign: 'center' }]}>Allowances</Text>
                        <Text style={[styles.th, styles.c_advances_amount, { textAlign: 'center' }]}>Advances</Text>
                        <Text style={[styles.th, styles.c_Total, { textAlign: 'center' }]}>Salary Payble</Text> */}
                    </View>
                </View>

                {/* Body */}
                <View>
                    {departments.map((dept, i) => (
                        <View key={i} wrap={false}>
                            {/* Department header */}
                            <View style={styles.deptHeader}>
                                <Text style={[styles.td, styles.c_dept, styles.deptTitle]}>
                                    {dept.deptName} ({dept.countEmployees})
                                </Text>
                                <Text style={[styles.td, styles.c_emp]}></Text>
                                <Text style={[styles.td, styles.c_desig]}></Text>
                                <Text style={[styles.td, styles.c_prWDays]}></Text>
                                <Text style={[styles.td, styles.c_lCount]}></Text>
                                <Text style={[styles.td, styles.c_lLeaves]}></Text>
                                <Text style={[styles.td, styles.c_empSalary]}></Text>
                                <Text style={[styles.td, styles.c_perDay]}></Text>
                                <Text style={[styles.td, styles.c_OT]}></Text>
                                <Text style={[styles.td, styles.c_OTApH]}></Text>
                                <Text style={[styles.td, styles.c_OTAmt]}></Text>
                                <Text style={[styles.td, styles.c_GrossSalary]}></Text>
                                <Text style={[styles.td, styles.c_allowances_amount]}></Text>
                                <Text style={[styles.td, styles.c_advances_amount]}></Text>
                                <Text style={[styles.td, styles.c_Total]}></Text>
                            </View>

                            {/* Employees */}
                            {dept.rows.map((row, idx) => (
                                <View key={idx} style={styles.tr}>
                                    <Text style={[styles.td, styles.c_dept]}>{row.deptName || "-"}</Text>
                                    <Text style={[styles.td, styles.c_emp]}>{row.empName}</Text>
                                    <Text style={[styles.td, styles.c_desig]}>{row.desigName}</Text>
                                    <Text style={[styles.td, styles.c_prWDays]}>{safeNum(row.present_working_days)}</Text>
                                    <Text style={[styles.td, styles.c_lCount]}>{safeNum(row.late_days)}</Text>
                                    <Text style={[styles.td, styles.c_lLeaves]}>{safeNum(row.late_leaves)}</Text>
                                    <Text style={[styles.td, styles.c_empSalary]}>{safeNum(row.empSalary).toLocaleString("en-US")}</Text>
                                    <Text style={[styles.td, styles.c_perDay]}>{Math.round(safeNum(row.per_day_salary)).toLocaleString("en-US")}</Text>
                                    <Text style={[styles.td, styles.c_OT]}>{row.overtime_hhmm || "00:00:00"}</Text>
                                    <Text style={[styles.td, styles.c_OTApH]}>{Math.round(safeNum(row.per_hour_ot_rate)).toLocaleString("en-US")}</Text>
                                    <Text style={[styles.td, styles.c_OTAmt]}>{Math.round(safeNum(row.overtime_amount)).toLocaleString("en-US")}</Text>
                                    <Text style={[styles.td, styles.c_GrossSalary]}>{Math.round(safeNum(row.total_pay_gross)).toLocaleString("en-US")}</Text>
                                    <Text style={[styles.td, styles.c_allowances_amount]}>{Math.round(safeNum(row.allowances_amount)).toLocaleString("en-US")}</Text>
                                    <Text style={[styles.td, styles.c_advances_amount]}>{Math.round(safeNum(row.advances_amount)).toLocaleString("en-US")}</Text>
                                    <Text style={[styles.td, styles.c_Total]}>{Math.round(safeNum(row.total_pay_net)).toLocaleString("en-US")}</Text>
                                </View>
                            ))}

                            {/* Department Subtotal */}
                            <View style={styles.subtotalRow}>
                                <Text style={[styles.td, styles.c_dept, styles.bold]}>Subtotal — {dept.deptName}</Text>
                                <Text style={[styles.td, styles.c_emp]}></Text>
                                <Text style={[styles.td, styles.c_desig]}></Text>
                                <Text style={[styles.td, styles.c_prWDays, styles.bold]}>{safeNum(dept.totals.present_working_days).toLocaleString("en-US")}</Text>
                                <Text style={[styles.td, styles.c_lCount, styles.bold]}>{safeNum(dept.totals.late_days).toLocaleString("en-US")}</Text>
                                <Text style={[styles.td, styles.c_lLeaves, styles.bold]}>{safeNum(dept.totals.late_leaves).toLocaleString("en-US")}</Text>
                                <Text style={[styles.td, styles.c_empSalary, styles.bold]}>{Math.round(safeNum(dept.totals.empSalary), 0).toLocaleString("en-US")}</Text>
                                <Text style={[styles.td, styles.c_perDay]}></Text>
                                <Text style={[styles.td, styles.c_OT, styles.bold]}>{dept.totals.overtime_hhmm_total || "00:00:00"}</Text>
                                <Text style={[styles.td, styles.c_OTApH]}></Text>
                                <Text style={[styles.td, styles.c_OTAmt, styles.bold]}>{Math.round(safeNum(dept.totals.overtime_amount), 0).toLocaleString("en-US")}</Text>
                                <Text style={[styles.td, styles.c_GrossSalary, styles.bold]}>{Math.round(safeNum(dept.totals.total_pay_gross), 0).toLocaleString("en-US")}</Text>
                                <Text style={[styles.td, styles.c_allowances_amount, styles.bold]}>{Math.round(safeNum(dept.totals.allowances_amount), 0).toLocaleString("en-US")}</Text>
                                <Text style={[styles.td, styles.c_advances_amount, styles.bold]}>{Math.round(safeNum(dept.totals.advances_amount), 0).toLocaleString("en-US")}</Text>
                                <Text style={[styles.td, styles.c_Total, styles.bold]}>{Math.round(safeNum(dept.totals.total_pay_net), 0).toLocaleString("en-US")}</Text>
                            </View>
                        </View>
                    ))}

                    {/* --- GRAND TOTAL --- */}
                    <View style={[styles.totalRow, { borderBottomWidth: 1, borderColor: "#bbb", marginTop: 6 }]}>
                        <Text style={[styles.td, styles.c_dept, styles.bold]}>GRAND TOTAL</Text>
                        <Text style={[styles.td, styles.c_emp]}></Text>
                        <Text style={[styles.td, styles.c_prWDays, styles.bold]}>{Math.round(safeNum(grandTotals.present_working_days), 0).toLocaleString("en-US")}</Text>
                        <Text style={[styles.td, styles.c_lCount, styles.bold]}>{Math.round(safeNum(grandTotals.late_days), 0).toLocaleString("en-US")}</Text>
                        <Text style={[styles.td, styles.c_lLeaves, styles.bold]}>{Math.round(safeNum(grandTotals.late_leaves), 0).toLocaleString("en-US")}</Text>
                        <Text style={[styles.td, styles.c_empSalary, styles.bold]}>{Math.round(safeNum(grandTotals.empSalary), 0).toLocaleString("en-US")}</Text>
                        <Text style={[styles.td, styles.c_perDay]}></Text>
                        <Text style={[styles.td, styles.c_OT, styles.bold]}>{grandTotals.overtime_hhmm_total || "00:00:00"}</Text>
                        <Text style={[styles.td, styles.c_OTApH]}></Text>
                        <Text style={[styles.td, styles.c_OTAmt, styles.bold]}>{Math.round(safeNum(grandTotals.overtime_amount), 0).toLocaleString("en-US")}</Text>
                        <Text style={[styles.td, styles.c_GrossSalary, styles.bold]}>{Math.round(safeNum(grandTotals.total_pay_gross), 0).toLocaleString("en-US")}</Text>
                        <Text style={[styles.td, styles.c_allowances_amount, styles.bold]}>{Math.round(safeNum(grandTotals.allowances_amount), 0).toLocaleString("en-US")}</Text>
                        <Text style={[styles.td, styles.c_advances_amount, styles.bold]}>{Math.round(safeNum(grandTotals.advances_amount), 0).toLocaleString("en-US")}</Text>
                        <Text style={[styles.td, styles.c_Total, styles.bold]}>{Math.round(safeNum(grandTotals.total_pay_net), 0).toLocaleString("en-US")}</Text>
                    </View>
                </View>

                {/* Footer */}
                <View fixed style={styles.footerBar}>
                    <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
                    <Link src="https://whdevs.com">Developed By : WHD</Link>
                </View>
            </Page>
        </Document >
    );
};

export default function SalarySheetReportPDF({ data, fileName, reportDate, company, title }) {
    const now = new Date();
    const defaultName = fileName || `Salary Sheet ${now.toISOString().split("T")[0]}.pdf`;

    return (
        <PDFDownloadLink
            document={
                <AttendanceReportDoc
                    data={data}
                    title={title}
                    company={company}
                    reportDate={reportDate}
                />
            }
            fileName={defaultName}
            style={{
                padding: "8px 14px",
                backgroundColor: "#1E90FF",
                border: "1px solid #1976d2",
                borderRadius: 6,
                textDecoration: "none",
                color: "#FFF",
                fontSize: 20,
            }}
        >
            {({ loading }) => (loading ? "Preparing PDF..." : "Print Salary Sheet")}
        </PDFDownloadLink>
    );
}
